import { ReferencedEntity, SelectedFile } from "./types";
import type { ToolOption } from "./components/ChatInputBar";
import type { ChatBackendKind } from "./selectChatBackend";
import type { ChatBackendInterface } from "./ChatBackendInterface";
import type { OutgoingChatImage } from "./chatImageAttachments";
import type { ThinkingEffort } from "./thinkingEffort";
import fpFetch from "../../../services/fpFetch";
import { BASE_API_URL, BASE_API_URL_V2 } from "../../../constants";
import { currentViewerTimeZone } from "@/utils/viewerTimeZone";
import { CHAT_TURN_ID_PREFIX } from "@/services/citationDeepLink";

// A chat conversation's backend `_id` is the house pgid
// (`pgid-chat-conversations-<uuid>`); the API and websocket room key on it.
// The URL, however, shows only the bare UUID. These two helpers convert
// between the URL slug (uuid) and the API id (pgid) at the routing boundary,
// so the backend keeps its convention while `/chat/<uuid>` stays clean. The
// prefix is fixed and deterministic for this table, so the strip is lossless.
const CHAT_CONVERSATION_ID_PREFIX = "pgid-chat-conversations-";

// pgid `_id` -> bare uuid slug for the URL. Already-bare input passes through.
export const toConversationSlug = (id: string): string =>
  id.startsWith(CHAT_CONVERSATION_ID_PREFIX)
    ? id.slice(CHAT_CONVERSATION_ID_PREFIX.length)
    : id;

// bare uuid slug -> pgid `_id` for API calls. Already-full input (an existing
// /chat/pgid-... link) passes through unchanged for back-compat.
export const fromConversationSlug = (slug: string): string =>
  slug.startsWith(CHAT_CONVERSATION_ID_PREFIX)
    ? slug
    : `${CHAT_CONVERSATION_ID_PREFIX}${slug}`;

// Whether the surface hides Delete on a message (#12426). A route that says
// nothing follows the backend: agentic chat can delete the last message of a
// branch, the legacy backend's delete stays off as it was. A route that says
// something is obeyed (the Notetaker view keeps Delete off, the Matrix panels
// keep it on).
export const isDeleteDisabled = (
  kind: ChatBackendKind,
  disableDelete: boolean | undefined
): boolean => disableDelete ?? kind !== "agentic";

// Turn ids follow the same house convention (`pgid-chat-turns-<uuid>`), and
// the same rule applies to URLs (#11499): the RMS Source chip's `?turn=`
// anchor carries only the bare uuid, converted back at the routing boundary.
// The prefix itself is owned by the citation deep-link service (#11608),
// which shares the `?turn=` vocabulary for document citation links.

export const toTurnSlug = (id: string): string =>
  id.startsWith(CHAT_TURN_ID_PREFIX)
    ? id.slice(CHAT_TURN_ID_PREFIX.length)
    : id;

export const fromTurnSlug = (slug: string): string =>
  slug.startsWith(CHAT_TURN_ID_PREFIX) ? slug : `${CHAT_TURN_ID_PREFIX}${slug}`;

// The Source chip's turn anchor from a URL query string, expanded back to
// the full turn id — or null when the URL carries none. Pure so the anchor
// decision is unit-testable outside the scroll effect it drives.
export const anchorTurnIdFromSearch = (search: string): string | null => {
  const slug = new URLSearchParams(search).get("turn");
  return slug ? fromTurnSlug(slug) : null;
};

// Which rendered message a turn anchor should land on: the turn's user
// message (it carries the authorization), the AI reply as backstop, then a
// bare-id message (the live-streaming path). Null when none is rendered.
export const pickAnchorMessageKey = (
  turnId: string,
  hasKey: (key: string) => boolean
): string | null =>
  [`${turnId}-user`, `${turnId}-assistant`, turnId].find(hasKey) ?? null;

// Per-submit state carried from the starter page to the chat page via
// React Router location state. The chat page consumes it on first mount
// after `handleStartChat` to fire the initial query without going through
// a URL-string round-trip. Adding a field here forces both producer
// (handleStartChat) and consumer (ChatConversation resume effect) to be
// updated via the type checker.
export interface PendingChatSend {
  message: string;
  selectedFiles: SelectedFile[];
  selectedFolderPaths: string[][];
  selectedTool: ToolOption;
  // The thinking effort level (#12389); null = no choice (the turn runs at Medium).
  thinkingEffort: ThinkingEffort | null;
  // The skill the user slash-invoked for this message (#9789); null = none.
  skillId: string | null;
  // The RMS records the message @-mentioned (#12154); empty = none.
  referencedEntities: ReferencedEntity[];
  // The id minted when the starter sent the message (#13890). Router state
  // survives a reload, so the first send can fire again from it; the same id
  // lets the server answer that copy instead of running it twice.
  clientMessageId?: string;
}

// React Router location state shape for /chat/:workspaceId.
export interface ChatLocationState {
  pendingSend?: PendingChatSend;
  // Which backend created the conversation being opened. A merged-history click
  // carries this so the chat page resolves to the right store regardless of the
  // org's current flag (#8147). Absent on a cold load → fall back to the flag.
  chatBackendKind?: ChatBackendKind;
}

// Build the executeChatQuery options that fire the starter's first send.
// Lives next to ExecuteChatOptions so every field mapping (selectedTool →
// isWebSearch, thinkingEffort → thinking_effort) is in one place —
// adding a new per-submit field requires updating both ends of this map
// and the type checker enforces it.
export const buildExecuteChatOptions = (
  pendingSend: PendingChatSend
): ExecuteChatOptions => ({
  thinking_effort: pendingSend.thinkingEffort,
  ...(pendingSend.selectedTool === "web" ? { isWebSearch: true } : {}),
  ...(pendingSend.skillId ? { skill_id: pendingSend.skillId } : {}),
  ...(pendingSend.referencedEntities.length > 0
    ? { referenced_entities: pendingSend.referencedEntities }
    : {}),
  ...(pendingSend.clientMessageId
    ? { client_message_id: pendingSend.clientMessageId }
    : {}),
});

export interface ExecuteChatOptions {
  isWebSearch?: boolean;
  retried_from_query_id?: string;
  preceding_query_id?: string;
  // The turn Send now stopped to send this message (#11986); persisted on the
  // message's event so the interrupted banner stays hidden after a reload.
  superseded_turn_id?: string;
  // Edit-user-message: send as a branch (parent = preceding_query_id verbatim,
  // null ⇒ root sibling) rather than a linear append to head. (#8147)
  isBranch?: boolean;
  // Required: every chat send must explicitly state the user's thinking
  // effort level (#12389), null for no choice (Medium). A missing value
  // used to default to Extended, which silently overrode the user's choice on
  // paths that forgot to thread it.
  thinking_effort: ThinkingEffort | null;
  // The skill the user slash-invoked for this message (#9789). Agentic backend
  // only; the legacy query path ignores it.
  skill_id?: string;
  // The RMS records the user @-mentioned in this message (#12154). Agentic
  // backend only; the legacy query path ignores them.
  referenced_entities?: ReferencedEntity[];
  // Screenshots attached to this message (#11382), already base64-encoded.
  // Agentic backend only; the legacy query path ignores them.
  images?: OutgoingChatImage[];
  // The id minted when this message was composed or queued (#13890), sent
  // with every copy of it so the server runs it once. Agentic backend only;
  // the legacy query path ignores it.
  client_message_id?: string;
}

export const executeChatQuery = async (
  query: string,
  selectedFiles: SelectedFile[],
  queryId: string,
  workspaceId: string,
  userId: string,
  options: ExecuteChatOptions,
  selectedFolderPaths: string[][] = []
) => {
  const {
    isWebSearch = false,
    retried_from_query_id,
    preceding_query_id,
    thinking_effort,
  } = options;
  // The legacy query path keeps its own on/off extended thinking; the top
  // agentic level is what the old Extended toggle meant (#12389).
  const extended_thinking = thinking_effort === "xhigh";

  const requestBody = {
    query,
    workspaceId,
    wsDiscriminator: "default",
    _id: queryId,
    selectedFiles,
    selectedFolderPaths,
    tabIndex: 1,
    quality: "standard",
    extended_thinking,
    // The viewer's IANA zone, read fresh on every send rather than stored, so
    // it follows the person across a flight. The backend states it (alongside
    // the organization's) in every prompt that asserts a date, and falls back
    // to the organization's calendar when it is absent or unusable (#11055).
    timezone: currentViewerTimeZone(),
    ...(isWebSearch ? { isWebSearch: true } : {}),
    ...(retried_from_query_id ? { retried_from_query_id } : {}),
    ...(preceding_query_id ? { preceding_query_id } : {}),
  };

  try {
    const response = await fpFetch(`${BASE_API_URL}/queryResults/fp0040a`, {
      ["x-finpilot-auth"]: "require",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestBody),
    });
    if (!response.ok) {
      throw new Error(`Query failed with status: ${response.status}`);
    }
    return queryId;
  } catch (error) {
    console.error("Error executing query:", error);
    throw error;
  }
};

export const updateThinkingTime = async (
  queryId: string,
  totalSeconds: number
) => {
  const requestBody = {
    total_seconds: totalSeconds,
  };

  try {
    const response = await fpFetch(
      `${BASE_API_URL_V2}/api/v2/chat/thinking/${queryId}/update-time`,
      {
        ["x-finpilot-auth"]: "require",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update thinking time: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error updating thinking time:", error);
    throw error;
  }
};

// The chat header's "Duplicate chat" handler for a given backend +
// conversation, or undefined when copying isn't possible — no conversation
// yet, or a backend that isn't a standalone conversation (the Matrix panel,
// the Setup wizard). On undefined the header drops its overflow menu rather
// than offering a copy that would fail (#10499, #11339). Resolves to the new
// conversation's URL slug.
export const cloneHandlerFor = (
  backend: Pick<ChatBackendInterface, "cloneWorkspace" | "slugFromId">,
  workspaceId: string | undefined
): (() => Promise<string>) | undefined => {
  const clone = backend.cloneWorkspace;
  if (!workspaceId || !clone) return undefined;
  return async () => backend.slugFromId(await clone(workspaceId));
};

// The server-side chart-renderer proxy's response: the rendered chart image
// plus the natural dimensions it was rendered at.
export interface RenderedChart {
  imageBase64: string;
  originalWidth: number;
  originalHeight: number;
}

// Render a chart spec to an image via the server-side chart-renderer proxy.
// The endpoint isn't backend-specific, so both chat backends delegate their
// `renderChart` here (#11155).
export const renderChartViaProxy = async (
  chartData: unknown,
  width: number,
  height: number
): Promise<RenderedChart> => {
  const response = await fpFetch(
    `${BASE_API_URL_V2}/api/v2/chat/render-chart`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chart: chartData, width, height }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to render chart: ${response.status}`);
  }

  return await response.json();
};
